// Image Card Component
// Displays user-uploaded images with profile picture icon for navigation

import React, { useState } from "react";
import {
  ImageCardContainer,
  ImageWrapper,
  Image,
  ImageOverlay,
  ProfileIcon,
  ImageInfo,
  ImageLikes,
  ImageDescription,
  ImageErrorFallback,
} from "../../../styles/SocialFeed/ImageCard.styles";

interface ImageCardProps {
  image: {
    id: string;
    userId: string;
    username: string;
    avatar: string;
    imageUrl: string;
    // Optional: real uploads have no stored dimensions, so the card measures
    // the image instead. Only supply these when they are actually known.
    width?: number;
    height?: number;
    timestamp: Date;
    likes?: number;
    description?: string;
  };
  onViewProfile: (userId: string) => void;
  onImageClick?: (image: ImageCardProps["image"]) => void;
}

// Height to reserve before the image has loaded, as a height:width ratio.
// Derived from the id so a tile keeps the same placeholder height across
// re-renders — Math.random() here would make the masonry columns jump on every
// state change. Biased portrait, which is what profile photos overwhelmingly
// are.
const placeholderRatio = (id: string): number => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) % 1000;
  return 1.0 + (hash % 40) / 100; // 1.00 – 1.39
};

export const ImageCard: React.FC<ImageCardProps> = ({
  image,
  onViewProfile,
  onImageClick,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [measuredRatio, setMeasuredRatio] = useState<number | null>(null);

  // Prefer explicit dimensions, then the image's own once it has loaded, then
  // the stable placeholder. This is what lets the grid show true aspect ratios
  // without inventing width/height values for real photos.
  const aspectRatio =
    image.width && image.height
      ? image.height / image.width
      : measuredRatio ?? placeholderRatio(image.id);
  // Passed through as a ratio, not a pixel height. The tile's real width is
  // whatever the masonry column gives it, and only CSS knows that number.

  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onViewProfile(image.userId);
  };

  const handleImageWrapperClick = () => {
    if (onImageClick) {
      onImageClick(image);
    }
  };

  return (
    <ImageCardContainer $ratio={aspectRatio}>
      <ImageWrapper onClick={handleImageWrapperClick}>
        {!imageError ? (
          <Image
            src={image.imageUrl}
            alt={image.description || `Image by ${image.username}`}
            loading="lazy"
            onLoad={(e) => {
              const el = e.currentTarget;
              if (el.naturalWidth > 0) {
                setMeasuredRatio(el.naturalHeight / el.naturalWidth);
              }
              setImageLoaded(true);
            }}
            onError={() => setImageError(true)}
            $loaded={imageLoaded}
          />
        ) : (
          <ImageErrorFallback>
            Image unavailable
          </ImageErrorFallback>
        )}
        <ImageOverlay>
          <ProfileIcon
            onClick={handleProfileClick}
            $avatarUrl={image.avatar}
            title={`View ${image.username}'s profile`}
          />
          {image.likes !== undefined && (
            <ImageLikes>❤️ {image.likes}</ImageLikes>
          )}
        </ImageOverlay>
        {image.description && (
          <ImageInfo>
            <ImageDescription>{image.description}</ImageDescription>
          </ImageInfo>
        )}
      </ImageWrapper>
    </ImageCardContainer>
  );
};

